import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, NotificationDot, Section } from '@platform/ui';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import { formatBoardDateTime } from '@/features/board';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useInquiryList } from '../api/use-inquiry.query.ts';
import {
  INQUIRY_CATEGORIES,
  INQUIRY_CATEGORY_LABEL,
  INQUIRY_PAGE_SIZE,
  INQUIRY_STATUS_CLASS,
  INQUIRY_STATUS_LABEL,
} from '../constants.ts';
import type {
  Inquiry,
  InquiryCategory,
  InquiryStatus,
} from '../inquiry.type.ts';

const chip =
  'rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors';
const chipActive = 'border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950';
const chipIdle =
  'border-black/15 dark:border-white/15 bg-white dark:bg-dark-card text-neutral-950/65 dark:text-white/65 hover:border-neutral-950 hover:text-neutral-950 dark:text-neutral-100';

function shouldShowInquiryListDot(inquiry: Inquiry): boolean {
  return inquiry.status === 'ANSWERED' && inquiry.answerReadAt === null;
}

/**
 * 1:1 문의 목록 (본인 문의 / 운영자=전체).
 * AuthGuard 하위 라우트라 비로그인 진입은 차단된다.
 *
 * 협업(파트너십) 문의는 별도 공개 페이지로 안내한다(비로그인 제출 가능).
 */
export function InquiryListView() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<InquiryCategory | null>(null);
  const [status, setStatus] = useState<InquiryStatus | null>(null);

  // 검색 — 입력은 즉시, 질의는 디바운스(350ms)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, isFetching, refetch } = useInquiryList({
    page,
    size: INQUIRY_PAGE_SIZE,
    category,
    status,
    search: search || undefined,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / INQUIRY_PAGE_SIZE));

  const seo = buildSeo({
    title: '1:1 문의',
    description: '문의 내역을 확인하고 새 문의를 남겨보세요.',
    path: PATHS.SUPPORT_INQUIRIES,
  });

  const resetTo = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <>
      <SEO {...seo} />
      <main className="overflow-x-hidden bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="mx-auto max-w-4xl">
              {/* 헤더 */}
              <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <span className="text-[12px] font-semibold uppercase tracking-[0.16em] text-neutral-950/40">
                    Support
                  </span>
                  <h1 className="mt-2 text-[clamp(28px,3.5vw,44px)] font-extrabold -tracking-[0.03em]">
                    1:1 문의
                  </h1>
                  <p className="mt-2 text-[14px] text-neutral-950/55">
                    남기신 문의와 답변을 확인할 수 있습니다.
                  </p>
                </div>
                <Link
                  to={PATHS.inquiryWrite()}
                  className="inline-flex h-11 items-center gap-1.5 rounded-full bg-neutral-950 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200 px-6 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                  <PlusIcon />
                  문의하기
                </Link>
              </header>

              {/* 협업 문의 안내 배너 */}
              <Link
                to={PATHS.SUPPORT_COLLABORATION}
                className="group mt-7 flex items-center justify-between gap-4 rounded-2xl border border-black/10 dark:border-white/10 bg-neutral-950 px-5 py-4 text-white transition-colors hover:bg-neutral-800 sm:px-6"
              >
                <div className="min-w-0">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
                    Partnership
                  </span>
                  <p className="mt-0.5 truncate text-[15px] font-semibold">
                    기업·관공서·교육기관 협업 문의
                  </p>
                  <p className="mt-0.5 truncate text-[13px] text-white/55">
                    도입 컨설팅·단체 라이선스·커스터마이징 — 로그인 없이 바로
                    접수
                  </p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-[13px] font-semibold transition-colors group-hover:bg-white/25">
                  협업 문의하기
                  <ArrowRight />
                </span>
              </Link>

              {/* 검색 + 필터 */}
              <div className="mt-8 flex flex-col gap-4 border-b border-black/10 dark:border-white/10 pb-6">
                <div className="relative">
                  <SearchIcon />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="제목으로 검색"
                    className="w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-dark-card py-3 pl-11 pr-4 text-[14px] text-neutral-950 dark:text-neutral-100 outline-none transition-colors placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-neutral-950"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => resetTo(() => setCategory(null))}
                    className={`${chip} ${category === null ? chipActive : chipIdle}`}
                  >
                    전체
                  </button>
                  {INQUIRY_CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => resetTo(() => setCategory(c.value))}
                      className={`${chip} ${category === c.value ? chipActive : chipIdle}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {([null, 'PENDING', 'ANSWERED'] as const).map((s) => (
                    <button
                      key={s ?? 'all'}
                      type="button"
                      onClick={() => resetTo(() => setStatus(s))}
                      className={`${chip} ${status === s ? chipActive : chipIdle}`}
                    >
                      {s === null ? '상태 전체' : INQUIRY_STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>

              {/* 결과 카운트 */}
              {!isLoading && !isError && (
                <p className="mt-5 text-[13px] text-neutral-500 dark:text-neutral-400">
                  총 <span className="font-semibold text-neutral-950 dark:text-neutral-100">{total}</span>건
                </p>
              )}

              {/* 목록 */}
              <div className="mt-3">
                {isLoading ? (
                  <SkeletonList />
                ) : isError ? (
                  <StateBox>
                    <p className="text-[14px] text-neutral-950/60">
                      목록을 불러오지 못했습니다.
                    </p>
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="mt-3 rounded-full border border-black/15 dark:border-white/15 px-5 py-2 text-[13px] font-semibold transition-colors hover:border-neutral-950"
                    >
                      다시 시도
                    </button>
                  </StateBox>
                ) : items.length === 0 ? (
                  <StateBox>
                    <p className="text-[15px] font-semibold text-neutral-950 dark:text-neutral-100">
                      {search || category || status
                        ? '조건에 맞는 문의가 없습니다.'
                        : '아직 문의 내역이 없습니다.'}
                    </p>
                    <p className="mt-1 text-[13px] text-neutral-950/55">
                      궁금한 점이 있다면 새 문의를 남겨주세요.
                    </p>
                    <Link
                      to={PATHS.inquiryWrite()}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-neutral-950 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200 px-5 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                    >
                      <PlusIcon />
                      문의하기
                    </Link>
                  </StateBox>
                ) : (
                  <ul
                    className={`flex flex-col gap-2.5 transition-opacity ${
                      isFetching ? 'opacity-60' : ''
                    }`}
                  >
                    {items.map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          onClick={() => navigate(PATHS.inquiryDetail(it.id))}
                          className="group flex w-full flex-col gap-2 rounded-2xl border border-black/10 dark:border-white/10 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-neutral-950/30 hover:shadow-[0_6px_24px_-12px_rgba(0,0,0,0.25)] sm:p-5"
                        >
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-black/[0.04] dark:bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-950/60">
                              {INQUIRY_CATEGORY_LABEL[it.category]}
                            </span>
                            <span className="relative">
                              <span
                                className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${INQUIRY_STATUS_CLASS[it.status]}`}
                              >
                                {INQUIRY_STATUS_LABEL[it.status]}
                              </span>
                              {shouldShowInquiryListDot(it) ? (
                                <span className="absolute -right-0.5 -top-1.5 z-10">
                                  <NotificationDot
                                    role="img"
                                    aria-label="확인하지 않은 답변"
                                    color="var(--color-red-500)"
                                    ringWidth={0}
                                    size={8}
                                    animated={false}
                                  />
                                </span>
                              ) : null}
                            </span>
                            <span className="ml-auto text-[12px] text-neutral-400 dark:text-neutral-500">
                              {formatBoardDateTime(it.createdAt)}
                            </span>
                          </div>
                          <p className="text-[15px] font-semibold text-neutral-950 dark:text-neutral-100 group-hover:underline">
                            {it.title}
                          </p>
                          {it.excerpt && (
                            <p className="line-clamp-1 text-[13px] text-neutral-950/55">
                              {it.excerpt}
                            </p>
                          )}
                          {it.status === 'ANSWERED' && it.answeredAt && (
                            <p className="text-[12px] font-medium text-emerald-600">
                              답변 완료 · {formatBoardDateTime(it.answeredAt)}
                            </p>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-full border border-black/15 dark:border-white/15 px-4 py-2 text-[13px] font-semibold transition-colors hover:border-neutral-950 disabled:opacity-40 disabled:hover:border-black/15 dark:border-white/15"
                  >
                    이전
                  </button>
                  <span className="px-2 text-[13px] text-neutral-600 dark:text-neutral-300">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-full border border-black/15 dark:border-white/15 px-4 py-2 text-[13px] font-semibold transition-colors hover:border-neutral-950 disabled:opacity-40 disabled:hover:border-black/15 dark:border-white/15"
                  >
                    다음
                  </button>
                </div>
              )}
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}

function StateBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/15 dark:border-white/15 py-20 text-center">
      {children}
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="flex flex-col gap-2.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="animate-pulse rounded-2xl border border-black/10 dark:border-white/10 p-5"
        >
          <div className="flex gap-2">
            <div className="h-4 w-16 rounded-full bg-black/[0.06] dark:bg-white/10" />
            <div className="h-4 w-14 rounded-full bg-black/[0.06] dark:bg-white/10" />
          </div>
          <div className="mt-3 h-4 w-2/3 rounded bg-black/[0.06] dark:bg-white/10" />
          <div className="mt-2 h-3 w-1/2 rounded bg-black/[0.05] dark:bg-white/10" />
        </li>
      ))}
    </ul>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      <path
        d="M7 2v10M2 7h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <path
        d="M4 2.5L7.5 6L4 9.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden
    >
      <circle
        cx="7"
        cy="7"
        r="5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M11 11L14 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
